import React, { useCallback, useEffect, useRef, useState } from "react";
import { useMap, MapControl, ControlPosition } from "@vis.gl/react-google-maps";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../framework/customStore";
import {
    clearError,
    setSelectedGeofenceId,
    saveGeofenceThunk,
    deleteGeofenceThunk,
    selectVisibleGeofences,
} from "../../../store/geofenceSlice";
import {
    GeofenceDto,
    GeofenceShapeType,
    CircleShapeData,
    RectangleShapeData,
    PolygonShapeData,
    SaveGeofenceDto,
    parseShapeData,
} from "../../../models/geofence/geofence";
import { GeofenceSaveDialog } from "./geofenceSaveDialog";

const GEOFENCE_COLOR = "#1565c0";
const GEOFENCE_SELECTED_COLOR = "#e65100";

type ShapeOverlay =
    | google.maps.Circle
    | google.maps.Rectangle
    | google.maps.Polygon;

const createShapeOverlay = (
    geofence: GeofenceDto,
    map: google.maps.Map,
    isSelected: boolean
): ShapeOverlay | null => {
    const shapeData = parseShapeData(geofence);
    if (!shapeData) return null;

    const color = isSelected ? GEOFENCE_SELECTED_COLOR : GEOFENCE_COLOR;
    const commonOptions = {
        map,
        strokeColor: color,
        strokeOpacity: 0.9,
        strokeWeight: 2,
        fillColor: color,
        fillOpacity: 0.12,
        zIndex: isSelected ? 2 : 1,
        editable: false,
        draggable: false,
    };

    if (geofence.shapeType === GeofenceShapeType.Circle) {
        const data = shapeData as CircleShapeData;
        return new google.maps.Circle({
            ...commonOptions,
            center: { lat: data.centerLat, lng: data.centerLng },
            radius: data.radiusMeters,
        });
    } else if (geofence.shapeType === GeofenceShapeType.Rectangle) {
        const data = shapeData as RectangleShapeData;
        return new google.maps.Rectangle({
            ...commonOptions,
            bounds: {
                north: data.northEastLat,
                east: data.northEastLng,
                south: data.southWestLat,
                west: data.southWestLng,
            },
        });
    } else {
        const data = shapeData as PolygonShapeData;
        return new google.maps.Polygon({
            ...commonOptions,
            paths: data.points,
        });
    }
};

interface TooltipState {
    x: number;
    y: number;
    name: string;
}

export const GeofenceOverlay = () => {
    const map = useMap();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const {
        geofences,
        showOnMap,
        selectedGeofenceId,
        drawingMode,
        editMode,
        saving,
        error,
    } = useAppSelector((s) => s.geofence);

    const visibleGeofences = useAppSelector(selectVisibleGeofences);

    const shapesRef = useRef<Map<string, ShapeOverlay>>(new Map());
    const tooltipHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editGeofence, setEditGeofence] = useState<GeofenceDto | null>(null);
    const [editShapeData, setEditShapeData] = useState<string>("");
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [tooltip, setTooltip] = useState<TooltipState | null>(null);

    // Clear edit state when editMode turns off or drawing starts
    useEffect(() => {
        if (!editMode || drawingMode) {
            setEditGeofence(null);
            setEditShapeData("");
            setEditDialogOpen(false);
            setDeleteConfirmOpen(false);
        }
    }, [editMode, drawingMode]);

    // Keep editGeofence in sync when the geofence is updated in the store
    useEffect(() => {
        if (!editGeofence) return;
        const updated = geofences.find(
            (g) => g.geofenceId === editGeofence.geofenceId
        );
        if (updated && updated !== editGeofence) setEditGeofence(updated);
    }, [geofences, editGeofence]);

    const clearEdit = useCallback(() => {
        dispatch(setSelectedGeofenceId(null));
        setEditGeofence(null);
        setEditShapeData("");
    }, [dispatch]);

    const handleSaveShape = useCallback(() => {
        if (!editGeofence) return;
        const dto: SaveGeofenceDto = {
            geofenceId: editGeofence.geofenceId,
            name: editGeofence.name,
            description: editGeofence.description,
            customerId: editGeofence.customerId,
            costCenterId: editGeofence.costCenterId,
            shapeType: editGeofence.shapeType,
            shapeData: editShapeData || editGeofence.shapeData,
            rowId: editGeofence.rowId,
        };
        dispatch(saveGeofenceThunk(dto)).then((result) => {
            if (saveGeofenceThunk.fulfilled.match(result)) {
                clearEdit();
            }
        });
    }, [editGeofence, editShapeData, dispatch, clearEdit]);

    const handleDeleteConfirmed = useCallback(() => {
        if (!editGeofence) return;
        setDeleting(true);
        setDeleteError(null);
        dispatch(deleteGeofenceThunk(editGeofence.geofenceId)).then((result) => {
            setDeleting(false);
            if (deleteGeofenceThunk.fulfilled.match(result)) {
                setDeleteConfirmOpen(false);
                clearEdit();
            } else {
                setDeleteError(t("monitorView.geofence.deleteError"));
            }
        });
    }, [editGeofence, dispatch, t, clearEdit]);

    // Rebuild shapes when relevant state changes
    useEffect(() => {
        if (!map) return;

        setTooltip(null);
        shapesRef.current.forEach((s) => s.setMap(null));
        shapesRef.current.clear();

        const visible = showOnMap;
        if (!visible) return;

        visibleGeofences.forEach((geofence) => {
            const isSelected = geofence.geofenceId === selectedGeofenceId;
            const shape = createShapeOverlay(geofence, map, isSelected);
            if (!shape) return;

            // Editable/draggable only when selected in editMode (not drawing)
            if (isSelected && editMode && !drawingMode) {
                shape.setOptions({ editable: true, draggable: true });

                const updateShapeData = () => {
                    try {
                        if (geofence.shapeType === GeofenceShapeType.Circle) {
                            const c = shape as google.maps.Circle;
                            setEditShapeData(
                                JSON.stringify({
                                    centerLat: c.getCenter().lat(),
                                    centerLng: c.getCenter().lng(),
                                    radiusMeters: c.getRadius(),
                                })
                            );
                        } else if (
                            geofence.shapeType === GeofenceShapeType.Rectangle
                        ) {
                            const r = shape as google.maps.Rectangle;
                            const ne = r.getBounds().getNorthEast();
                            const sw = r.getBounds().getSouthWest();
                            setEditShapeData(
                                JSON.stringify({
                                    northEastLat: ne.lat(),
                                    northEastLng: ne.lng(),
                                    southWestLat: sw.lat(),
                                    southWestLng: sw.lng(),
                                })
                            );
                        } else {
                            const p = shape as google.maps.Polygon;
                            const pts: { lat: number; lng: number }[] = [];
                            p.getPath().forEach((ll) =>
                                pts.push({ lat: ll.lat(), lng: ll.lng() })
                            );
                            setEditShapeData(
                                JSON.stringify({ points: pts })
                            );
                        }
                    } catch {
                        /* ignore */
                    }
                };

                if (geofence.shapeType === GeofenceShapeType.Circle) {
                    const c = shape as google.maps.Circle;
                    c.addListener("center_changed", updateShapeData);
                    c.addListener("radius_changed", updateShapeData);
                } else if (
                    geofence.shapeType === GeofenceShapeType.Rectangle
                ) {
                    shape.addListener("bounds_changed", updateShapeData);
                } else {
                    const p = shape as google.maps.Polygon;
                    p.getPath().addListener("set_at", updateShapeData);
                    p.getPath().addListener("insert_at", updateShapeData);
                }
                shape.addListener("dragend", updateShapeData);
            }

            // Click: in editMode select geofence for editing; otherwise just select
            shape.addListener("click", () => {
                if (editMode && !drawingMode) {
                    dispatch(setSelectedGeofenceId(geofence.geofenceId));
                    setEditGeofence(geofence);
                    setEditShapeData(geofence.shapeData);
                } else {
                    dispatch(setSelectedGeofenceId(geofence.geofenceId));
                }
            });

            // Hover tooltip — delayed hide prevents flicker on rapid mouseout/mouseover
            const showTooltip = (e: google.maps.MapMouseEvent) => {
                if (tooltipHideTimeoutRef.current) {
                    clearTimeout(tooltipHideTimeoutRef.current);
                    tooltipHideTimeoutRef.current = null;
                }
                const dom = e.domEvent as MouseEvent;
                setTooltip({ x: dom.clientX + 14, y: dom.clientY - 28, name: geofence.name });
            };
            shape.addListener("mouseover", showTooltip);
            shape.addListener("mousemove", showTooltip);
            shape.addListener("mouseout", () => {
                tooltipHideTimeoutRef.current = setTimeout(() => {
                    setTooltip(null);
                    tooltipHideTimeoutRef.current = null;
                }, 80);
            });

            shapesRef.current.set(geofence.geofenceId, shape);
        });

        return () => {
            if (tooltipHideTimeoutRef.current) {
                clearTimeout(tooltipHideTimeoutRef.current);
                tooltipHideTimeoutRef.current = null;
            }
            shapesRef.current.forEach((s) => {
                google.maps.event.clearInstanceListeners(s);
                s.setMap(null);
            });
            shapesRef.current.clear();
            setTooltip(null);
        };
    }, [
        map,
        visibleGeofences,
        showOnMap,
        selectedGeofenceId,
        editMode,
        drawingMode,
    ]);

    const showActionBar =
        editMode && !drawingMode && editGeofence !== null && !editDialogOpen;

    return (
        <>
            {tooltip && (
                <div
                    style={{
                        position: "fixed",
                        left: tooltip.x,
                        top: tooltip.y,
                        backgroundColor: "rgba(0,0,0,0.72)",
                        color: "#fff",
                        padding: "3px 8px",
                        borderRadius: 4,
                        fontSize: 12,
                        pointerEvents: "none",
                        zIndex: 9999,
                        whiteSpace: "nowrap",
                    }}
                >
                    {tooltip.name}
                </div>
            )}
            {showActionBar && (
                <MapControl position={ControlPosition.TOP_CENTER}>
                    <Paper
                        elevation={3}
                        sx={{
                            m: 1,
                            px: 1.5,
                            py: 0.75,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, mr: 0.5 }}
                        >
                            {editGeofence.name}
                        </Typography>
                        <ButtonGroup size="small" variant="outlined">
                            <Button
                                onClick={() => setEditDialogOpen(true)}
                            >
                                {t("monitorView.geofence.editDetails")}
                            </Button>
                            <Button
                                onClick={handleSaveShape}
                                disabled={saving}
                            >
                                {saving ? (
                                    <CircularProgress size={16} />
                                ) : (
                                    t("monitorView.geofence.saveShape")
                                )}
                            </Button>
                            <Button
                                color="error"
                                onClick={() => setDeleteConfirmOpen(true)}
                            >
                                {t("common.remove")}
                            </Button>
                            <Button onClick={clearEdit}>
                                {t("common.cancel")}
                            </Button>
                        </ButtonGroup>
                    </Paper>
                </MapControl>
            )}
            {editDialogOpen && editGeofence && (
                <GeofenceSaveDialog
                    open={editDialogOpen}
                    shapeType={editGeofence.shapeType}
                    shapeData={editShapeData || editGeofence.shapeData}
                    editGeofence={editGeofence}
                    onClose={() => {
                        setEditDialogOpen(false);
                        clearEdit();
                    }}
                />
            )}
            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                maxWidth="xs"
            >
                <DialogTitle>
                    {t("monitorView.geofence.deleteTitle")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t("monitorView.geofence.deleteConfirm", {
                            name: editGeofence?.name,
                        })}
                    </DialogContentText>
                    {deleteError && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                            {deleteError}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>
                        {t("common.cancel")}
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleDeleteConfirmed}
                        disabled={deleting}
                    >
                        {deleting ? (
                            <CircularProgress size={16} />
                        ) : (
                            t("common.remove")
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={!!error}
                autoHideDuration={5000}
                onClose={() => dispatch(clearError())}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity="error" onClose={() => dispatch(clearError())}>
                    {error ? t(error) : null}
                </Alert>
            </Snackbar>
        </>
    );
};
